import { format, isValid, parse } from 'date-fns';

import type { DateOperatorFields, DateOperatorFilter, LineItemEndDateFilter, RangeFilter } from '../../../apis/graphql';
import { DateOperator } from '../../../apis/graphql';
import type { SearchParamsValuesType } from '../../../constants';
import { TRAFFICKING_SEARCH_PARAMS } from '../../../constants';
import { getRelativeExpression } from '../FilterPanel/FilterRow/DateControl/relativeDateLabels';
import { computeRelativeRange, formatRangeBound } from '../FilterPanel/FilterRow/DateControl/relativeDateRange';
import { isComparisonDateOperator } from '../FilterPanel/FilterRow/DateControl/types';
import type { FilterValue, IncludeExcludeOptions } from '../TableFilterBar/AdditionalFilters/constants';
import { DEFAULT_BOOLEAN_OPTIONS } from '../TableFilterBar/AdditionalFilters/constants';
import {
  isBooleanFilterValue,
  isCalendarDateFilterValue,
  isDropdownOptionValues,
  isExistenceComparisonOperation,
  isLineItemEndDateFilterValue,
  isRangeFilterValue,
  isSimpleDropdownValues,
} from '../TableFilterBar/AdditionalFilters/utils';

export const CLEAR_ALL_BUTTON_LABEL = 'Clear Filters';

const CALENDAR_OPERATOR_LABELS: Record<string, string> = {
  IS: 'Is',
  IS_BEFORE: 'Is before',
  IS_AFTER: 'Is after',
  IS_ON_OR_BEFORE: 'Is on or before',
  IS_ON_OR_AFTER: 'Is on or after',
  IS_BETWEEN: 'Is Between',
  IS_RELATIVE_TO_TODAY: 'Is Relative to Today',
};

const OPERATOR_SYMBOLS: Record<string, string> = {
  EQUAL: '=',
  NOT_EQUAL: '≠',
  GREATER_THAN: '>',
  GREATER_THAN_OR_EQUAL: '≥',
  LESS_THAN: '<',
  LESS_THAN_OR_EQUAL: '≤',
};

// KATANA-1811: an existence filter has no value to format, so the chip reads "Weight: is empty".
// Copy pending Product alongside OPERATOR_LABELS in OperatorValueRangeControl.
const EXISTENCE_CHIP_TEXT: Record<string, string> = {
  IS_EMPTY: 'is empty',
  IS_NOT_EMPTY: 'is not empty',
};

const getRangeValue = (
  value: RangeFilter,
  searchParam: SearchParamsValuesType,
  valuePrefix?: string | null,
  valueSuffix?: string | null,
  displayMultiplier?: number | null,
  valueDecimals?: number | null
): string[] => {
  // Checked first, before any bound: the operator is the whole filter, and a stray bound beside it
  // is dropped by campaign-graphql before forwarding, so it must not show on the chip either.
  if (isExistenceComparisonOperation(value.minOperator)) {
    return [EXISTENCE_CHIP_TEXT[value.minOperator as string]];
  }

  if (searchParam === TRAFFICKING_SEARCH_PARAMS.COMPLETIONS_REMAINING) {
    if (value.max != null) {
      const symbol = OPERATOR_SYMBOLS[value.maxOperator as string] ?? OPERATOR_SYMBOLS.LESS_THAN;
      return [`${symbol} ${value.max}`];
    }
    if (value.min != null) {
      const symbol = OPERATOR_SYMBOLS[value.minOperator as string] ?? OPERATOR_SYMBOLS.GREATER_THAN;
      return [`${symbol} ${value.min}`];
    }
    return [];
  }

  const formatValue = (v: number): string => {
    const displayValue = displayMultiplier ? v * displayMultiplier : v;
    const rounded =
      displayMultiplier && valueDecimals != null
        ? Number((v * displayMultiplier).toFixed(valueDecimals))
        : displayValue;
    const formatted = String(rounded);
    const withPrefix = valuePrefix ? `${valuePrefix}${formatted}` : formatted;
    return valueSuffix ? `${withPrefix}${valueSuffix}` : withPrefix;
  };

  const { min, max } = value;

  if (min != null && max != null && min !== max) {
    return [`Min: ${formatValue(min)}`, `Max: ${formatValue(max)}`];
  }
  if (min != null && max != null && min === max) {
    return [`= ${formatValue(min)}`];
  }
  if (min != null) {
    const symbol = OPERATOR_SYMBOLS[value.minOperator as string] ?? '≥';
    return [`${symbol} ${formatValue(min)}`];
  }
  if (max != null) {
    const symbol = OPERATOR_SYMBOLS[value.maxOperator as string] ?? '≤';
    return [`${symbol} ${formatValue(max)}`];
  }
  return [];
};

const formatChipDate = (d: string | null | undefined): string => {
  if (!d) return '';
  const parsed = parse(d, 'yyyy-MM-dd', new Date());
  return isValid(parsed) ? format(parsed, 'MM/dd/yyyy') : d;
};

const getLineItemEndDateValue = (value: LineItemEndDateFilter): string => {
  const operation =
    value.comparisonOperation
      ?.split('_')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ') ?? '';
  const rawUnit = value.unit ? value.unit.charAt(0).toUpperCase() + value.unit.slice(1).toLowerCase() : '';
  const unit = `${rawUnit}${value.duration !== 1 ? 's' : ''}`;
  return `${operation} ${value.duration} ${unit}`;
};

const formatRelativeChipText = (filter: DateOperatorFilter): string | null => {
  if (!filter.direction || !filter.unit) return null;
  const expression = getRelativeExpression(filter.direction, filter.value ?? null, filter.unit);
  const range = computeRelativeRange({
    direction: filter.direction,
    value: filter.value ?? null,
    unit: filter.unit,
    referenceDate: filter.referenceDate,
  });
  const startFmt = range ? formatRangeBound(range.start) : '';
  const endFmt = range ? formatRangeBound(range.end) : '';
  const rangeBounds = startFmt === endFmt ? startFmt : startFmt + ' – ' + endFmt;
  const rangeStr = range ? ' (' + rangeBounds + ')' : '';
  return `${expression}${rangeStr}`;
};

const getCalendarDateValues = (value: DateOperatorFields): string[] => {
  const label = value.operator ? CALENDAR_OPERATOR_LABELS[value.operator] ?? value.operator : '';

  if (value.operator === DateOperator.IsRelativeToToday) {
    const text = formatRelativeChipText(value as DateOperatorFilter);
    return [text ? text.trim() : ''];
  }

  // KATANA-1752: a comparison operator (Is before/after/on or before/on or after) can carry a
  // conditional (relative) boundary instead of a fixed date — same DateOperatorFilter shape the
  // IS_RELATIVE_TO_TODAY branch above already reads, just keyed off the comparison operator
  // instead. Falling through to formatChipDate(value.date) below would render this as just the
  // bare operator label, since a conditional value has no `.date`. Unflagged, same rationale as
  // DateControl's displayValue — the live FilterContext value can be a ConditionalDateFilter
  // regardless of the authoring flag.
  if (value.operator && isComparisonDateOperator(value.operator)) {
    const text = formatRelativeChipText(value as DateOperatorFilter);
    if (text) return [`${label} ${text}`.trim()];
  }

  const dateStr =
    value.operator === 'IS_BETWEEN'
      ? `${formatChipDate(value.startDate)} – ${formatChipDate(value.endDate)}`
      : formatChipDate(value.date);
  return [`${label} ${dateStr}`.trim()];
};

export const getChipTextValue = (
  value: FilterValue,
  searchParam: SearchParamsValuesType,
  valuePrefix?: string | null,
  valueSuffix?: string | null,
  displayMultiplier?: number | null,
  valueDecimals?: number | null
): string => {
  if (isRangeFilterValue(value)) {
    return getRangeValue(value, searchParam, valuePrefix, valueSuffix, displayMultiplier, valueDecimals).join(', ');
  }

  if (isLineItemEndDateFilterValue(value)) {
    return getLineItemEndDateValue(value);
  }

  if (isCalendarDateFilterValue(value)) {
    return getCalendarDateValues(value).join(', ');
  }

  if (isDropdownOptionValues(value)) {
    return value.map(({ value, label }) => `${label}: ${value}`).join(', ');
  }

  if (isSimpleDropdownValues(value)) {
    return value.join(', ');
  }

  if (isBooleanFilterValue(value)) {
    return value ? DEFAULT_BOOLEAN_OPTIONS.yes : DEFAULT_BOOLEAN_OPTIONS.no;
  }

  return '';
};

export const getChipLabel = (
  defaultLabel: string,
  currentSearchParam: string,
  includeExcludeOptions?: IncludeExcludeOptions
): string => {
  if (!includeExcludeOptions) return defaultLabel;

  if (currentSearchParam === includeExcludeOptions.exclude) return `${defaultLabel} (Exclude)`;

  return `${defaultLabel} (Include)`;
};
